import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from './utils/logger';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored && ['light', 'dark', 'high-contrast'].includes(stored)) {
        return stored;
      }
      return 'light';
    }
    return 'light';
  });

  // Set theme on document element and handle automatic detection
  useEffect(() => {
    // Set data-theme attribute for CSS variable overrides
    document.documentElement.setAttribute('data-theme', theme);
    
    // Maintain backward compatibility with 'dark' class
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Persist to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Automatic theme detection on mount
  useEffect(() => {
    // Read stored preference once — any valid stored value is explicit (including 'light')
    const stored = localStorage.getItem('theme');
    const hasExplicitPreference = stored && ['light', 'dark', 'high-contrast'].includes(stored);

    // Only auto-detect if user hasn't explicitly chosen a theme
    if (!hasExplicitPreference) {
      // Check for system high-contrast preference first (has precedence)
      const prefersHighContrast = window.matchMedia && window.matchMedia('(prefers-contrast: high)').matches;
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      // High-contrast takes precedence over dark mode
      if (prefersHighContrast) {
        setThemeState('high-contrast');
      } else if (prefersDark) {
        setThemeState('dark');
      }
    }

    // Listen for system preference changes
    const contrastMediaQuery = window.matchMedia('(prefers-contrast: high)');
    const colorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleContrastChange = (e) => {
      if (e.matches) {
        const stored = localStorage.getItem('theme');
        const hasExplicitPreference = stored && ['light', 'dark', 'high-contrast'].includes(stored);
        // High-contrast preference takes precedence, but don't override explicit user choice
        if (!hasExplicitPreference) {
          setThemeState('high-contrast');
        }
      }
    };

    const handleColorSchemeChange = (e) => {
      if (e.matches) {
        const stored = localStorage.getItem('theme');
        const hasExplicitPreference = stored && ['light', 'dark', 'high-contrast'].includes(stored);
        // Only set dark mode if high-contrast is not preferred and user hasn't explicitly chosen
        if (!hasExplicitPreference) {
          const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
          if (!prefersHighContrast) {
            setThemeState('dark');
          }
        }
      }
    };

    // Modern browsers
    if (contrastMediaQuery.addEventListener) {
      contrastMediaQuery.addEventListener('change', handleContrastChange);
      colorSchemeMediaQuery.addEventListener('change', handleColorSchemeChange);
    } else {
      // Fallback for older browsers
      contrastMediaQuery.addListener(handleContrastChange);
      colorSchemeMediaQuery.addListener(handleColorSchemeChange);
    }

    return () => {
      if (contrastMediaQuery.removeEventListener) {
        contrastMediaQuery.removeEventListener('change', handleContrastChange);
        colorSchemeMediaQuery.removeEventListener('change', handleColorSchemeChange);
      } else {
        contrastMediaQuery.removeListener(handleContrastChange);
        colorSchemeMediaQuery.removeListener(handleColorSchemeChange);
      }
    };
  }, []);

  const setTheme = (newTheme) => {
    if (!['light', 'dark', 'high-contrast'].includes(newTheme)) {
      logger.warn(`Invalid theme: ${newTheme}. Must be one of: light, dark, high-contrast`);
      return;
    }
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
export default ThemeProvider;
